import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, lazy, Suspense, type ReactNode } from "react";
import { useIdleMount } from "@/hooks/use-idle-mount";
import { resolveInitialLocale, LocaleProvider, useLocale } from "@/lib/i18n/locale-context";
import { I18nProvider, getI18n } from "@/lib/i18n";
import { htmlLang, type Lang } from "@/lib/i18n/config";

// Sidoinnehåll som inte behövs för första målningen laddas efter sidan är i gång.
const AppExternalLinks = lazy(() =>
  import("@/app-bridge/components/AppExternalLinks").then((m) => ({ default: m.AppExternalLinks })),
);
const AppPrivacyGate = lazy(() =>
  import("@/components/legal/AppPrivacyGate").then((m) => ({ default: m.AppPrivacyGate })),
);
const CookieConsent = lazy(() =>
  import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent })),
);
const ConsentGate = lazy(() =>
  import("@/components/legal/ConsentGate").then((m) => ({ default: m.ConsentGate })),
);
const ResumeDraftBanner = lazy(() =>
  import("@/components/ResumeDraftBanner").then((m) => ({ default: m.ResumeDraftBanner })),
);
const DeletionPendingGate = lazy(() =>
  import("@/components/DeletionPendingGate").then((m) => ({ default: m.DeletionPendingGate })),
);
const ScanJobDock = lazy(() =>
  import("@/components/scan/ScanJobDock").then((m) => ({ default: m.ScanJobDock })),
);
const ScanStageOverlay = lazy(() =>
  import("@/components/scan/ScanStageOverlay").then((m) => ({ default: m.ScanStageOverlay })),
);
const AdminPreviewBar = lazy(() =>
  import("@/components/admin/AdminPreviewBar").then((m) => ({ default: m.AdminPreviewBar })),
);
const SiteAnalytics = lazy(() =>
  import("@/components/SiteAnalytics").then((m) => ({ default: m.SiteAnalytics })),
);
const QueryPersistence = lazy(() =>
  import("@/components/QueryPersistence").then((m) => ({ default: m.QueryPersistence })),
);
const AppLockGate = lazy(() =>
  import("@/app-bridge/components/AppLockGate").then((m) => ({ default: m.AppLockGate })),
);
const AppBadgeSync = lazy(() =>
  import("@/app-bridge/components/AppBadgeSync").then((m) => ({ default: m.AppBadgeSync })),
);
const AppDeviceSync = lazy(() =>
  import("@/app-bridge/components/AppDeviceSync").then((m) => ({ default: m.AppDeviceSync })),
);

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

import { NavHistoryTracker } from "@/components/NavHistoryTracker";
import { ScrollToTop } from "@/components/ScrollToTop";
const AppRoutePrefetch = lazy(() =>
  import("@/app-bridge/components/AppRoutePrefetch").then((m) => ({ default: m.AppRoutePrefetch })),
);

import { MobileTabBar } from "@/components/MobileTabBar";
import { useAppShell } from "@/app-bridge/hooks/useAppShell";
import { cn } from "@/lib/utils";
import { RouteProgress } from "@/components/RouteProgress";
import { OfflineBanner } from "@/app-bridge/components/OfflineBanner";
import { IconSpriteDefs } from "@/components/icons/IconSprite";

import { ScanJobProvider } from "@/components/scan/ScanJobProvider";

// Aviseringarna laddas efter första målningen – de behövs aldrig direkt.
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
import { registerServiceWorker } from "@/lib/pwa";

/** Språk vid felvyer: följer sidans prefix, oberoende av providers. */
function errorLang(): Lang {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return pathname.startsWith("/en") ? "en" : pathname.startsWith("/es") ? "es" : "sv";
}

function NotFoundComponent() {
  const t = getI18n(errorLang()).t.bind(getI18n(errorLang()));
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t("errors.notFoundTitle")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("errors.notFoundBody")}</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("common.toStartPage")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  const t = getI18n(errorLang()).t.bind(getI18n(errorLang()));
  const message = error?.message ?? "";
  // Tre olika lägen: tappat nät, för många besökare, eller ett riktigt fel.
  const isOffline =
    typeof navigator !== "undefined" && navigator.onLine === false
      ? true
      : /failed to fetch|networkerror|load failed/i.test(message);
  const isBusy = /\b(429|503)\b|too many requests|rate limit/i.test(message);

  const title = isOffline
    ? t("errors.offlineTitle")
    : isBusy
      ? t("errors.busyTitle")
      : t("errors.errorTitle");
  const body = isOffline
    ? t("errors.offlineBody")
    : isBusy
      ? t("errors.busyBody")
      : t("errors.errorBody");

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              void import("@/app-bridge/core/connectivity").then((m) => m.verifyConnection());
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("common.tryAgain")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("common.toStartPage")}
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Backend-domänen. Webbläsaren hinner öppna anslutningen (DNS + TLS) medan
 * sidan renderas, så första datahämtningen slipper vänta på handskakningen.
 */
const API_ORIGIN = (() => {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL as string).origin;
  } catch {
    return null;
  }
})();

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Språk och land läses ur kakor på servern (och i webbläsaren) så att
  // rätt språk finns med i HTML:en vid första svaret.
  beforeLoad: async ({ location }) => resolveInitialLocale(location.pathname),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Sellavo — sälj snabbt och snyggt" },
      {
        name: "description",
        content:
          "Sälj dina prylar snabbt och snyggt på Sellavo. Fota, få färdig annonstext och prisförslag, och lägg ut gratis.",
      },
      { name: "author", content: "Sellavo" },
      { name: "theme-color", content: "#2d8a9e" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Sellavo" },
      { name: "mobile-web-app-capable", content: "yes" },

      { property: "og:title", content: "Sellavo — sälj snabbt och snyggt" },
      {
        property: "og:description",
        content:
          "Sälj dina prylar snabbt och snyggt på Sellavo. Fota, få färdig annonstext och prisförslag, och lägg ut gratis.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Sellavo" },
      { property: "og:locale", content: "sv_SE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@sellavo" },
    ],
    links: [
      ...(API_ORIGIN
        ? [
            { rel: "preconnect", href: API_ORIGIN, crossOrigin: "anonymous" as const },
            { rel: "dns-prefetch", href: API_ORIGIN },
          ]
        : []),
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Typsnitten ligger lokalt och förladdas så rubriken kan ritas direkt.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/outfit-latin.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/figtree-latin.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "icon", href: "/icons/icon-192.png", type: "image/png", sizes: "192x192" },
      { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://sellavo.com/#organization",
              name: "Sellavo",
              url: "https://sellavo.com",
              logo: "https://sellavo.com/icons/icon-192.png",
              description:
                "Svensk marknadsplats för begagnade prylar där AI skriver annonsen och föreslår pris.",
              areaServed: "SE",
            },
            {
              "@type": "WebSite",
              "@id": "https://sellavo.com/#website",
              name: "Sellavo",
              url: "https://sellavo.com",
              inLanguage: "sv-SE",
              publisher: { "@id": "https://sellavo.com/#organization" },
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: "https://sellavo.com/annonser?q={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Körs före första målningen så att rätt utseende ritas direkt – ingen omställning efteråt.
const themeBootstrap = `(function(){try{
var m=localStorage.getItem('sellavo-theme-mode')||localStorage.getItem('saljavo-theme-mode')||'auto';
var dark;
if(m==='dark'){dark=true}else if(m==='light'){dark=false}else{
var lat=59.33,lon=18.07;try{var c=JSON.parse(localStorage.getItem('sellavo-home-coords')||localStorage.getItem('saljavo-home-coords')||'null');if(c&&typeof c.lat==='number'){lat=c.lat;lon=c.lon}}catch(e){}
var d=new Date(),D=Math.floor((d-new Date(d.getFullYear(),0,0))/864e5);
var decl=-23.44*Math.cos(2*Math.PI/365*(D+10))*Math.PI/180,la=lat*Math.PI/180;
var cosH=(Math.cos(90.833*Math.PI/180)-Math.sin(la)*Math.sin(decl))/(Math.cos(la)*Math.cos(decl));
if(cosH<=-1){dark=false}else if(cosH>=1){dark=true}else{
var H=Math.acos(cosH)*180/Math.PI/15;
var solarNoon=12-lon/15;var utcH=d.getUTCHours()+d.getUTCMinutes()/60;
dark=!(utcH>solarNoon-H&&utcH<solarNoon+H);}}
var e=document.documentElement;
e.classList.toggle('dark',dark);
e.style.colorScheme=dark?'dark':'light';
}catch(e){}})();`;

// Körs innan appens modul startar. Safari kan efter en uppdatering hålla kvar
// en gammal modulreferens och då blir sidan annars helt blank. Rensa bara
// Sellavos app-cache och försök en gång med en unik sid-URL.
const moduleRecoveryBootstrap = `(function(){
var key='sellavo-module-recovery';
function isModuleFailure(value){return /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|unable to preload css/i.test(String(value||''));}
function fallback(){
var current=document.getElementById('sellavo-module-fallback');if(current)return;
var box=document.createElement('div');box.id='sellavo-module-fallback';box.setAttribute('role','alert');box.style.cssText='position:fixed;inset:0;z-index:2147483647;display:grid;place-items:center;padding:24px;background:#f8fafc;color:#0f172a;font:16px system-ui,sans-serif;text-align:center';
box.innerHTML='<div><strong style="display:block;font-size:20px;margin-bottom:8px">Sidan behöver laddas om</strong><span style="display:block;margin-bottom:18px;color:#475569">En ny version av Sellavo finns tillgänglig.</span><button type="button" style="border:0;border-radius:8px;padding:11px 18px;background:#16798a;color:white;font:600 15px system-ui,sans-serif">Ladda om</button></div>';
box.querySelector('button').addEventListener('click',function(){try{sessionStorage.removeItem(key);}catch(e){}location.reload();});(document.body||document.documentElement).appendChild(box);
}
function recover(){
var previous=0;try{previous=Number(sessionStorage.getItem(key)||0);if(Date.now()-previous<30000){fallback();return;}sessionStorage.setItem(key,String(Date.now()));}catch(e){}
var cleanup=[];
try{if('serviceWorker'in navigator)cleanup.push(navigator.serviceWorker.getRegistrations().then(function(items){return Promise.allSettled(items.filter(function(item){var worker=item.active||item.waiting||item.installing;return worker&&new URL(worker.scriptURL,location.href).pathname==='/sw.js';}).map(function(item){return item.unregister();}));}));}catch(e){}
try{if('caches'in window)cleanup.push(caches.keys().then(function(names){return Promise.allSettled(names.filter(function(name){return name.indexOf('sellavo-pages-')===0||name.indexOf('sellavo-assets-')===0||name.indexOf('precache')!==-1;}).map(function(name){return caches.delete(name);}));}));}catch(e){}
Promise.allSettled(cleanup).finally(function(){var url=new URL(location.href);url.searchParams.set('__module_retry',Date.now().toString());location.replace(url.toString());});
}
window.addEventListener('vite:preloadError',function(event){event.preventDefault();recover();});
window.addEventListener('unhandledrejection',function(event){if(isModuleFailure(event.reason&&event.reason.message||event.reason)){event.preventDefault();recover();}});
window.addEventListener('error',function(event){var target=event.target;var moduleScript=target&&target.tagName==='SCRIPT'&&target.type==='module';if(moduleScript||isModuleFailure(event.message)){recover();}},true);
window.addEventListener('load',function(){window.setTimeout(function(){try{sessionStorage.removeItem(key);var url=new URL(location.href);if(url.searchParams.has('__module_retry')){url.searchParams.delete('__module_retry');history.replaceState(history.state,'',url.pathname+url.search+url.hash);}}catch(e){}},3000);});
})();`;

function RootShell({ children }: { children: ReactNode }) {
  const { lang } = Route.useRouteContext();
  return (
    <html lang={htmlLang(lang)} suppressHydrationWarning>
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: themeBootstrap }} />
        <script dangerouslySetInnerHTML={{ __html: moduleRecoveryBootstrap }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient, lang, country } = Route.useRouteContext();
  // Allt som inte behövs för första intrycket monteras när webbläsaren är ledig.
  const idle = useIdleMount();
  // App-skalet (låst skärmhöjd + inre scroll) används bara i native-wrappern
  // eller installerad PWA. I en vanlig mobil webbläsare rullar dokumentet som
  // vanligt, annars glider adressfältet ur synk och det blir tomrum i botten.
  const appShell = useAppShell();

  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider lang={lang}>
        <LocaleProvider lang={lang} country={country}>
      {idle && (
        <Suspense fallback={null}>
          <QueryPersistence queryClient={queryClient} />
        </Suspense>
      )}
      <AuthProvider>
        <ThemeProvider>
          <ScanJobProvider>
            <RouteProgress />
            {idle && (
              <Suspense fallback={null}>
                <SiteAnalytics />
              </Suspense>
            )}
            <OfflineBanner />
            <IconSpriteDefs />

            {/* Appläge (wrapper/PWA) på mobil: app-skal med fast header/tabbar och
              scroll i mittytan. Webbläsare och desktop: vanlig sidscroll. */}
            <div
              className={cn(
                "flex flex-col bg-background md:h-auto md:min-h-screen md:overflow-visible",
                appShell ? "h-screen h-[100dvh] overflow-hidden" : "min-h-screen min-h-[100dvh]",
              )}
            >
              {idle && (
                <Suspense fallback={null}>
                  <AdminPreviewBar />
                </Suspense>
              )}
              <SiteHeader />
              <div
                id="app-scroll"
                className={cn(
                  "flex flex-1 flex-col pb-[var(--tabbar-space)] md:pb-0",
                  appShell
                    ? "overflow-y-auto overflow-x-hidden overscroll-contain [-webkit-overflow-scrolling:touch] md:overflow-visible"
                    : "overflow-x-clip",
                )}
              >
                <div className="flex-1">
                  {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
                  <Outlet />
                </div>
                <SiteFooter />
              </div>
            </div>

            <MobileTabBar />
            {idle && (
              <Suspense fallback={null}>
                <ScanJobDock />
                <ScanStageOverlay />
                <ResumeDraftBanner />
              </Suspense>
            )}
            <NavHistoryTracker />
            <ScrollToTop />
            <Suspense fallback={null}>
              <Toaster position="top-center" closeButton />
            </Suspense>
            {idle && (
              <Suspense fallback={null}>
                <CookieConsent />
                <AppPrivacyGate />
                <ConsentGate />
                <DeletionPendingGate />
                <AppDeviceSync />
                <AppBadgeSync />
                <AppRoutePrefetch />
                <AppExternalLinks />
              </Suspense>
            )}
          </ScanJobProvider>
          <Suspense fallback={null}>
            <AppLockGate />
          </Suspense>
        </ThemeProvider>
      </AuthProvider>
        </LocaleProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
